import * as React from "react"
import { cn } from "../../lib/utils"
import { Check, X } from "lucide-react"

export interface Option {
  value: string;
  label: string;
  tags?: string[];
}

interface MultiSelectProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

// Helper functions to extract complex conditions and logic
const isValidOption = (option: Option): boolean => {
  return !!(option && option.label);
};

const matchesSearchTerm = (option: Option, searchTerm: string): boolean => {
  if (!searchTerm) return true;
  
  const searchLower = searchTerm.toLowerCase();
  const labelMatches = option.label.toLowerCase().includes(searchLower);
  const tagMatches = option.tags?.some(tag => tag?.toLowerCase()?.includes(searchLower)) || false;
  
  return labelMatches || tagMatches;
};

const getFilteredOptions = (options: Option[], searchTerm: string): Option[] => {
  return options.filter(option => 
    isValidOption(option) && matchesSearchTerm(option, searchTerm)
  );
};

const isOptionSelected = (value: string[], optionValue: string): boolean => {
  return value.includes(optionValue);
};

const getNewValueAfterToggle = (value: string[], optionValue: string): string[] => {
  return isOptionSelected(value, optionValue)
    ? value.filter(v => v !== optionValue)
    : [...value, optionValue];
};

const getNewValueAfterRemove = (value: string[], optionValue: string): string[] => {
  return value.filter(v => v !== optionValue);
};

const getContainerClasses = (disabled: boolean | undefined, className?: string): string => {
  return cn(
    "min-h-[38px] w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm",
    "focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50",
    disabled && "bg-gray-100 cursor-not-allowed",
    className
  );
};

const getCheckboxClasses = (isSelected: boolean): string => {
  return cn(
    "h-4 w-4 rounded border flex items-center justify-center",
    isSelected ? "border-primary bg-primary" : "border-gray-300"
  );
};

// Extracted components for better separation of concerns
const SelectedItem: React.FC<{
  option: Option;
  onRemove: (value: string) => void;
}> = ({ option, onRemove }) => (
  <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-sm">
    {option.label}
    <button
      onClick={(e) => {
        e.stopPropagation();
        onRemove(option.value);
      }}
      className="text-gray-500 hover:text-gray-700"
    >
      <X className="h-3 w-3" />
    </button>
  </span>
);

const SelectedItemsDisplay: React.FC<{
  value: string[];
  options: Option[];
  placeholder: string;
  onRemoveValue: (value: string) => void;
}> = ({ value, options, placeholder, onRemoveValue }) => {
  if (value.length === 0) {
    return <span className="text-gray-500">{placeholder}</span>;
  }

  return (
    <>
      {value.map(v => {
        const option = options.find(o => o.value === v);
        return option ? (
          <SelectedItem key={v} option={option} onRemove={onRemoveValue} />
        ) : null;
      })}
    </>
  );
};

const SearchInput: React.FC<{
  searchTerm: string;
  onSearchChange: (value: string) => void;
}> = ({ searchTerm, onSearchChange }) => (
  <div className="p-2">
    <input
      type="text"
      className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-primary focus:outline-none"
      placeholder="Search..."
      value={searchTerm}
      onChange={(e) => onSearchChange(e.target.value)}
      onClick={(e) => e.stopPropagation()}
    />
  </div>
);

const OptionItem: React.FC<{
  option: Option;
  isSelected: boolean;
  onToggle: (value: string) => void;
}> = ({ option, isSelected, onToggle }) => (
  <li
    className={cn(
      "flex items-center px-3 py-2 text-sm cursor-pointer",
      "hover:bg-gray-100"
    )}
    onClick={() => onToggle(option.value)}
  >
    <div className="flex items-center gap-2">
      <div className={getCheckboxClasses(isSelected)}>
        {isSelected && <Check className="h-3 w-3 text-white" />}
      </div>
      {option.label}
    </div>
  </li>
);

const DropdownOptions: React.FC<{
  filteredOptions: Option[];
  value: string[];
  onToggleOption: (value: string) => void;
}> = ({ filteredOptions, value, onToggleOption }) => (
  <ul className="max-h-60 overflow-auto py-1">
    {filteredOptions.map((option) => (
      <OptionItem
        key={option.value}
        option={option}
        isSelected={isOptionSelected(value, option.value)}
        onToggle={onToggleOption}
      />
    ))}
  </ul>
);

const Dropdown: React.FC<{
  isOpen: boolean;
  disabled: boolean | undefined;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  filteredOptions: Option[];
  value: string[];
  onToggleOption: (value: string) => void;
}> = ({ isOpen, disabled, searchTerm, onSearchChange, filteredOptions, value, onToggleOption }) => {
  if (!isOpen || disabled) return null;

  return (
    <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-300 bg-white shadow-lg">
      <SearchInput searchTerm={searchTerm} onSearchChange={onSearchChange} />
      <DropdownOptions
        filteredOptions={filteredOptions}
        value={value}
        onToggleOption={onToggleOption}
      />
    </div>
  );
};

const MultiSelect = React.forwardRef<HTMLDivElement, MultiSelectProps>(
  ({ className, options, value, onChange, placeholder = "Select...", disabled, ...props }, ref) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState("");
    const containerRef = React.useRef<HTMLDivElement>(null);

    // Handle click outside to close dropdown
    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredOptions = getFilteredOptions(options, searchTerm);

    const handleToggleOption = (optionValue: string) => {
      const newValue = getNewValueAfterToggle(value, optionValue);
      onChange(newValue);
    };

    const handleRemoveValue = (optionValue: string) => {
      const newValue = getNewValueAfterRemove(value, optionValue);
      onChange(newValue);
    };

    const handleContainerClick = () => {
      if (!disabled) {
        setIsOpen(!isOpen);
      }
    };

    return (
      <div ref={containerRef} className={cn("relative", className)} {...props}>
        <div
          className={getContainerClasses(disabled, className)}
          onClick={handleContainerClick}
        >
          <div className="flex flex-wrap gap-1">
            <SelectedItemsDisplay
              value={value}
              options={options}
              placeholder={placeholder}
              onRemoveValue={handleRemoveValue}
            />
          </div>
        </div>

        <Dropdown
          isOpen={isOpen}
          disabled={disabled}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          filteredOptions={filteredOptions}
          value={value}
          onToggleOption={handleToggleOption}
        />
      </div>
    );
  }
);

MultiSelect.displayName = "MultiSelect";

export { MultiSelect };